// A seizure run's structural network, from its own files (dynamics-sim-v0 `header.network` binds
// network.json; ba-activity's distributed-v2): the selected parcel's strongest connections, read from
// its own row rather than design's placeholder ("Strongest to the left middle temporal gyrus, insula
// and supramarginal gyrus"). The matrix is symmetric normalised fibre density (1 is the run's densest
// pair, before the coupling multiplier), with tract lengths in mm; the model delays each tract by its
// length over the conduction speed (network.json's `delay_rule`).

import type { DynamicsSim } from '../data/sims/dynamics.ts';

export interface NetworkNode {
  index: number;
  source_id?: string;
  source_name?: string;
  /** The pack structure the node is, where the run's parcel map has one. */
  structure?: string | null;
}

export interface Network {
  format?: string;
  /** In the header's parcel order. */
  nodes: NetworkNode[];
  /** `weights[target][source]`; symmetric in this run. */
  weights: number[][];
  tract_lengths_mm?: number[][];
  coupling_multiplier?: number;
  coupling_parameters?: { Ks?: number; Kvf?: number; Kf?: number };
  conduction_speed_m_s?: number;
  zero_delays?: boolean;
  positive_undirected_edges?: number;
}

export interface Connection {
  /** The other node. */
  index: number;
  weight: number;
  lengthMm: number | null;
  /** The model's delay on the tract, ms. */
  delayMs: number | null;
}

const square = (m: unknown, n: number): m is number[][] =>
  Array.isArray(m) && m.length === n && m.every((r) => Array.isArray(r) && r.length === n && r.every((x) => typeof x === 'number'));

/** The run's network where its header binds one and the file holds a square matrix over its nodes; else null. */
export async function loadNetwork(sim: DynamicsSim): Promise<Network | null> {
  const rel = sim.header.network?.metadata;
  if (typeof rel !== 'string' || !rel) return null;
  const net = await sim.files.maybeJson<Network>(rel);
  if (!net || !Array.isArray(net.nodes)) return null;
  const n = net.nodes.length;
  if (!n || !square(net.weights, n)) return null;
  if (net.tract_lengths_mm !== undefined && !square(net.tract_lengths_mm, n)) return null;
  return net;
}

/** The node a pack structure is: the network's own node with that structure, else the run's parcel (the same order). */
export function nodeOf(net: Network, sim: DynamicsSim, structure: string): number | null {
  const i = net.nodes.findIndex((x) => x.structure === structure);
  if (i >= 0) return i;
  const p = sim.parcels.findIndex((x) => x.structure === structure);
  return p >= 0 && p < net.nodes.length ? p : null;
}

/** The model's delay on a tract, ms: its length over the conduction speed (mm over m/s is ms); zero where the run has no delays. */
export function delayMs(net: Network, lengthMm: number | null): number | null {
  if (net.zero_delays) return 0;
  const v = net.conduction_speed_m_s;
  return lengthMm !== null && v !== undefined && v > 0 ? lengthMm / v : null;
}

/** Node i's strongest connections by weight, self and empty pairs left out; ties by index. */
export function strongest(net: Network, i: number, n = 3): Connection[] {
  const out: Connection[] = [];
  (net.weights[i] ?? []).forEach((w, j) => {
    if (j === i || !(w > 0)) return;
    const l = net.tract_lengths_mm?.[i]?.[j];
    const lengthMm = typeof l === 'number' && l > 0 ? l : null;
    out.push({ index: j, weight: w, lengthMm, delayMs: delayMs(net, lengthMm) });
  });
  out.sort((a, b) => b.weight - a.weight || a.index - b.index);
  return out.slice(0, n);
}

/** How many other nodes i connects to. */
export function degree(net: Network, i: number): number {
  return (net.weights[i] ?? []).filter((w, j) => j !== i && w > 0).length;
}
